import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject } from '@angular/core';

import type { ResolvedTheme, ThemeMode } from './theme.models';
import { ThemeStore } from './theme.store';

/**
 * Applies the resolved theme to everything outside the Angular component tree.
 *
 * The `tuiTheme` attribute on <body> is Taiga UI's own concern — `provideTaiga`
 * drives it from `TUI_DARK_MODE`, which `provideTheme` rewires onto
 * `ThemeStore`. What is left over is handled here: the UA colour scheme used
 * for scrollbars and native widgets, and the mobile browser chrome colour.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly store = inject(ThemeStore);

  readonly mode = this.store.mode;
  readonly resolvedTheme = this.store.resolvedTheme;
  readonly isDark = this.store.isDark;

  constructor() {
    effect(() => this.apply(this.store.resolvedTheme()));
  }

  setMode(mode: ThemeMode): void {
    this.store.setMode(mode);
  }

  private apply(theme: ResolvedTheme): void {
    const root = this.document.documentElement;

    root.style.colorScheme = theme;
    root.dataset['theme'] = theme;

    const meta = this.document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');

    if (meta) {
      // Read the palette back out of Taiga's own custom property so the browser
      // chrome always matches whatever theme is loaded. Taiga scopes the dark
      // palette to <body> via the `tuiTheme` attribute, not to :root.
      const background = this.document.defaultView
        ?.getComputedStyle(this.document.body)
        .getPropertyValue('--tui-background-base')
        .trim();

      meta.content = background && background.length > 0 ? background : '';
    }
  }
}
